
import moment from 'moment-timezone'
import { mapGetters } from 'vuex'
import Calendar from '../parts/Calendar.vue'

export default {
    components: { Calendar },
    props: {
        // state shape: { method, date, time, agent, agents, notes }
        state: { type: Object, required: true },
        // List of agent ids to query for availability
        agentIds: { type: Array, default: () => [] },
        // Pre-fetched soonest day per method, shape: { phone: 'YYYY-MM-DD'|null, video: 'YYYY-MM-DD'|null }.
        // The orchestrator pre-warms this so the calendar shows the pill instantly.
        soonest: { type: Object, default: () => ({ phone: null, video: null }) },
        // Allowed methods (e.g. ['phone','video','email']) — defaults to all 3.
        allowedMethods: { type: Array, default: () => ['phone', 'video', 'email'] },
        aep: { type: Boolean, default: false },
        // The Step 1 screener answer, 'new_to_medicare' | 'plan_review'; drives
        // duration copy in the aep lede.
        reason: { type: String, default: null },
        timezone: { type: String, default: 'America/New_York' },
        sessionId: { type: String, required: true },
        // Payer / non-payer routing: the client's tier, passed to the Calendar.
        payer: { type: Boolean, default: null },
        // Step-level error (e.g. the slot was just taken). Shown inline here so
        // it's visible next to the calendar rather than only in the footer.
        error: { type: String, default: '' },
        // Agent pinned via ?agent=<id>. When set, the Time step shows a small
        // "Booking with {name}" note, mirroring the legacy one-page Book.vue.
        pinnedAgent: { type: Object, default: null },
        // #570: whether to ask a returning client if they'd like to keep working
        // with their current agent. The orchestrator computes the conditions.
        showKeepAgent: { type: Boolean, default: false },
        // The returning client's current agent (id + first/last name), shown in
        // the keep-agent prompt label.
        returningAgent: { type: Object, default: null },
        // #576: the client's soonest existing appointment ({datetime,
        // manage_token}) or null. When set, this step blocks a duplicate booking
        // and offers Reschedule / Keep-my-current-time instead.
        upcomingAppointment: { type: Object, default: null },
    },
    computed: {
        ...mapGetters(['getScheduleHelpText', 'isBetweenSep1Oct12']),
        // Segment lede: the duration promised here is the booked Acuity type's
        // real length — the aep types run 30 minutes, everything else 45.
        ledeText () {
            if (this.aep) {
                if (this.reason === 'plan_review') {
                    return 'Plan reviews run about 30 minutes. The earliest opening is about 48 hours out, so we have time to prepare.'
                }
                // Same 30-minute AEP appointment type, screener unanswered.
                return 'Calls run about 30 minutes with a licensed advisor. The earliest opening is about 48 hours out, so we have time to prepare.'
            }
            return 'Calls run about 45 minutes with a licensed advisor. The earliest opening is about 48 hours out, so we have time to prepare.'
        },
        // Date-aware turnaround copy for the survey tile description.
        surveyTileDesc () {
            return this.isBetweenSep1Oct12
                ? 'Answer by email. Written recommendation by October 15.'
                : 'Answer by email. Written recommendation within 3 business days.'
        },
        // F3: survey-only clients (no phone/video allowed) get a dedicated
        // heading/copy/reassurance layout instead of tiles + calendar.
        isF3SurveyOnly () {
            return this.aep && !this.hasCall && this.hasSurvey
        },
        f3SubCopy () {
            const dateSentence = this.isBetweenSep1Oct12
                ? 'Finish by October 12 and your advisor emails your written recommendation by October 15.'
                : 'Your advisor emails your written recommendation within 3 business days of your completed survey.'
            return `Answer a few questions about your current coverage, medications, and providers. ${dateSentence}`
        },
        // Hint shown above the calendar before a method is picked. Under aep
        // the video tile never renders (only Phone + AEP survey), so "phone or
        // video" would be inaccurate — use a method-agnostic prompt instead.
        methodHintText () {
            return this.aep
                ? 'Choose an option above to continue.'
                : 'Choose phone or video above to see open times.'
        },
        hasUpcoming () {
            return !!(this.upcomingAppointment && this.upcomingAppointment.datetime)
        },
        upcomingWhenLabel () {
            if (!this.hasUpcoming) return ''
            // parseZone keeps the appointment's stored offset so it reads as the
            // client booked it, not shifted into their current browser zone.
            return moment.parseZone(this.upcomingAppointment.datetime).format('dddd, MMMM D [at] h:mm A')
        },
        chosenLabel () {
            if (!this.state.time) return ''
            // Same zone the Calendar rendered the slot in -- not the browser's.
            return moment.tz(this.state.time, this.timezone).format('dddd, MMMM D [at] h:mm A')
        },
        // Empty allowedMethods means the orchestrator authoritatively resolved
        // them to none (a non-participating account) — block. Until resolved the
        // orchestrator passes the full default set, so this never trips early.
        blocked () { return !this.allowedMethods.length },
        hasPhone () { return this.allowedMethods.indexOf('phone') > -1 },
        hasVideo () { return this.allowedMethods.indexOf('video') > -1 },
        hasEmail () { return this.allowedMethods.indexOf('email') > -1 },
        hasSurvey () { return this.allowedMethods.indexOf('survey') > -1 },
        hasCall () { return this.hasPhone || this.hasVideo },
        isCall () { return this.state.method === 'phone' || this.state.method === 'video' },
        isAsync () { return this.state.method === 'email' || this.state.method === 'survey' },
        asyncSelectedLabel () {
            return this.state.method === 'survey' ? 'AEP survey' : 'Email consultation'
        },
        asyncReassuranceHtml () {
            if (this.state.method === 'survey') {
                return this.getScheduleHelpText('survey')
            }
            return 'Once you submit the email consultation request, you’ll be matched with one of our licensed advisors, who will reach out to you directly via email within one business day to provide personalized guidance and support. If you would like to speak with a licensed advisor, please select phone or video.'
        },
        // Async methods the client can switch to from the calendar fallback,
        // excluding the one already selected. Under aep, survey is a peer tile
        // above (not a fallback link), so it's excluded here entirely — and
        // the email hatch hides too (Aziz QA 2026-09-01): the AEP survey IS
        // the async path during AEP, and a parallel email consult would skip
        // the survey request that anchors the whole nudge cadence. Off-season
        // rendering is byte-identical.
        asyncAlternatives () {
            const alts = []
            if (this.hasEmail && this.state.method !== 'email' && !(this.aep && this.hasSurvey)) {
                alts.push({ value: 'email', cta: 'Request an email consultation instead' })
            }
            if (!this.aep && this.hasSurvey && this.state.method !== 'survey') {
                alts.push({ value: 'survey', cta: 'Request the AEP survey instead' })
            }
            return alts
        },
    },
    methods: {
        setMethod (m) {
            // Changing the method re-scopes the availability pool, so the slot
            // picked under the old one — and above all the ADVISOR picked with
            // it — is no longer valid. Advisors carry per-method flags
            // (agent_phone_consult / agent_video_consult) and Acuity attaches a
            // calendar only to the appointment types its owner offers, so
            // carrying a phone-only advisor into a video booking is a 400
            // invalid_calendar at /book/ (Sentry FLOW-BACKEND-1BY: it used to
            // surface as a 500 and the client never got booked). Only the
            // email/survey branch used to clear; phone↔video kept everything.
            // Same reasoning as setKeepAgent below, which re-scopes the pool too.
            //
            // Clicking the tile that's already selected must not wipe a choice.
            if (m === this.state.method) return
            // Release BEFORE clearing: releaseHold reads state.time/state.agent
            // to know which hold to drop, and the parent's update-schedule
            // handler assigns synchronously.
            this.$emit('release-hold')
            this.$emit('update-schedule', {
                ...this.state, method: m, date: null, time: null, agent: null, agents: null,
            })
        },
        setKeepAgent (val) {
            // Changing the agent preference re-scopes the availability pool, so
            // any held slot is no longer valid — clear it and release the hold.
            // The Calendar refetches automatically when its `agents` prop shifts.
            this.$emit('update-schedule', { ...this.state, keepAgent: val, date: null, time: null, agent: null, agents: null })
            this.$emit('release-hold')
        },
        onPickSlot (slot) {
            // Orchestrator places the Redis hold and updates heldUntil; we
            // optimistically reflect the chosen slot here.
            this.$emit('update-schedule', { ...this.state, time: slot.time, agent: slot.agent, agents: slot.agents })
            this.$emit('place-hold', slot)
        },
    },
}
